import { useState, useEffect } from 'react';

/**
 * Check if WebGL is available in the browser
 */
export function isWebGLAvailable(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    return gl !== null && gl !== undefined;
  } catch {
    return false;
  }
}

/**
 * Check if WebGL2 is available in the browser
 */
export function isWebGL2Available(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2');
    return gl !== null && gl !== undefined;
  } catch {
    return false;
  }
}

/**
 * Check if user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export type VisualizationMode = '3d' | '2d' | 'reduced';

/**
 * Hook to determine the best visualization mode based on device capabilities
 * and user preferences
 */
export function useVisualizationMode(): {
  mode: VisualizationMode;
  isLoading: boolean;
  supportsWebGL: boolean;
  reducedMotion: boolean;
} {
  const [state, setState] = useState<{
    mode: VisualizationMode;
    isLoading: boolean;
    supportsWebGL: boolean;
    reducedMotion: boolean;
  }>({
    mode: '2d',
    isLoading: true,
    supportsWebGL: false,
    reducedMotion: false,
  });

  useEffect(() => {
    const webglAvailable = isWebGLAvailable();
    const reducedMotion = prefersReducedMotion();

    let mode: VisualizationMode;
    if (reducedMotion) {
      mode = 'reduced';
    } else if (webglAvailable) {
      mode = '3d';
    } else {
      mode = '2d';
    }

    setState({
      mode,
      isLoading: false,
      supportsWebGL: webglAvailable,
      reducedMotion,
    });

    // Listen for changes to reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = (e: MediaQueryListEvent) => {
      const newReducedMotion = e.matches;
      setState(prev => ({
        ...prev,
        reducedMotion: newReducedMotion,
        mode: newReducedMotion ? 'reduced' : (prev.supportsWebGL ? '3d' : '2d'),
      }));
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return state;
}
